import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from './ui/Icons';
import { 
  LabelDefinition, 
  PRESET_TEMPLATES 
} from '@/types/label-budget';
import { 
  getLabelDefinitions, 
  saveLabelDefinitions 
} from '@/utils/label-budget-storage';

interface LabelSettingsProps {
  onUpdate?: () => void;
}

export const LabelSettings: React.FC<LabelSettingsProps> = ({ onUpdate }) => {
  const [definitions, setDefinitions] = useState<LabelDefinition[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = () => {
    const stored = getLabelDefinitions();
    setDefinitions(stored);
  };

  const showMessage = (type: 'success' | 'error' | 'info', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const handleAddDefinition = () => {
    const newDefinition: LabelDefinition = {
      id: `label-${Date.now()}`,
      name: '',
      order: definitions.length,
      type: 'text',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    setDefinitions([...definitions, newDefinition]);
    setIsEditing(true);
  };

  const handleUpdateDefinition = (id: string, updates: Partial<LabelDefinition>) => {
    setDefinitions(defs => 
      defs.map(def => 
        def.id === id 
          ? { ...def, ...updates, updatedAt: new Date().toISOString() }
          : def
      )
    );
  };

  const handleDeleteDefinition = (id: string) => {
    setDefinitions(defs => {
      const filtered = defs.filter(def => def.id !== id);
      // Reorder remaining definitions
      return filtered.map((def, index) => ({ ...def, order: index }));
    });
  };

  const handleMoveDefinition = (id: string, direction: 'up' | 'down') => {
    const index = definitions.findIndex(def => def.id === id);
    if (
      (direction === 'up' && index === 0) || 
      (direction === 'down' && index === definitions.length - 1)
    ) {
      return;
    }

    const newDefs = [...definitions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    // Swap positions
    [newDefs[index], newDefs[targetIndex]] = [newDefs[targetIndex], newDefs[index]];
    
    // Update order values
    newDefs[index].order = index;
    newDefs[targetIndex].order = targetIndex;
    
    setDefinitions(newDefs);
  };

  const handleSave = () => {
    // Validate definitions
    const hasEmptyNames = definitions.some(def => !def.name.trim());
    if (hasEmptyNames) {
      showMessage('error', 'All label names must be filled');
      return;
    }

    const hasDuplicateNames = definitions.some((def, i) => 
      definitions.some((other, j) => i !== j && def.name === other.name)
    );
    if (hasDuplicateNames) {
      showMessage('error', 'Label names must be unique');
      return;
    }

    if (definitions.length > 5) {
      showMessage('error', 'Maximum 5 labels allowed');
      return;
    }

    const saved = saveLabelDefinitions(definitions);
    if (saved) {
      showMessage('success', 'Label definitions saved successfully');
      setIsEditing(false);
      onUpdate?.();
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('labelSettingsChanged'));
    } else {
      showMessage('error', 'Failed to save label definitions');
    }
  };

  const handleCancel = () => {
    loadDefinitions();
    setIsEditing(false);
  };

  const handleApplyTemplate = (templateKey: keyof typeof PRESET_TEMPLATES) => {
    const template = PRESET_TEMPLATES[templateKey];
    const newDefinitions: LabelDefinition[] = template.map((item, index) => ({
      id: `label-${Date.now()}-${index}`,
      name: item.name,
      order: index,
      type: item.type,
      options: item.options,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }));
    setDefinitions(newDefinitions);
    setIsEditing(true);
  };

  const handleAddOption = (id: string) => {
    const def = definitions.find(d => d.id === id);
    if (def?.type === 'select') {
      const newOption = `Option ${(def.options?.length || 0) + 1}`;
      handleUpdateDefinition(id, {
        options: [...(def.options || []), newOption]
      });
    }
  };

  const handleUpdateOption = (id: string, optionIndex: number, value: string) => {
    const def = definitions.find(d => d.id === id);
    if (def?.type === 'select' && def.options) {
      const newOptions = [...def.options];
      newOptions[optionIndex] = value;
      handleUpdateDefinition(id, { options: newOptions });
    }
  };

  const handleDeleteOption = (id: string, optionIndex: number) => {
    const def = definitions.find(d => d.id === id);
    if (def?.type === 'select' && def.options) {
      const newOptions = def.options.filter((_, i) => i !== optionIndex);
      handleUpdateDefinition(id, { options: newOptions });
    }
  };

  return (
    <div className="p-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-gray-900 dark:text-white">
          Label Definitions
        </h3>
        {!isEditing && (
          <div className="flex gap-2">
            <select
              onChange={(e) => e.target.value && handleApplyTemplate(e.target.value as keyof typeof PRESET_TEMPLATES)}
              className="px-2 py-1 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md"
              defaultValue=""
            >
              <option value="">Apply Template...</option>
              <option value="corporate">Corporate Template</option>
              <option value="startup">Startup Template</option>
            </select>
          </div>
        )}
      </div>

      {message && (
        <div className={`mb-1.5 p-1.5 rounded-md text-xs ${
          message.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
            : message.type === 'error'
            ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
            : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-700'
        }`}>
          {message.text}
        </div>
      )}

      {definitions.length === 0 && !isEditing ? (
        <div className="text-center py-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            No label definitions configured
          </p>
          <Button onClick={handleAddDefinition} className="bg-blue-600 hover:bg-blue-700">
            <PlusIcon className="w-4 h-4 mr-2" />
            Add First Label
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {definitions.map((def, index) => (
            <div key={def.id} className="p-2 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600">
              <div className="flex items-start gap-2">
                {/* Move buttons */}
                <div className="flex flex-col gap-1 pt-1">
                  <button
                    onClick={() => handleMoveDefinition(def.id, 'up')}
                    disabled={index === 0 || !isEditing}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleMoveDefinition(def.id, 'down')}
                    disabled={index === definitions.length - 1 || !isEditing}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronDownIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Label configuration */}
                <div className="flex-1 space-y-1">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <Input
                      value={def.name}
                      onChange={(e) => handleUpdateDefinition(def.id, { name: e.target.value })}
                      placeholder="Label name"
                      disabled={!isEditing}
                      className="text-sm"
                    />
                    <select
                      value={def.type}
                      onChange={(e) => handleUpdateDefinition(def.id, { 
                        type: e.target.value as LabelDefinition['type'],
                        options: e.target.value === 'select' ? ['Option 1'] : undefined
                      })}
                      disabled={!isEditing}
                      className="px-2 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md"
                    >
                      <option value="text">Text</option>
                      <option value="select">Select</option>
                      <option value="number">Number</option>
                      <option value="currency">Currency</option>
                    </select>
                  </div>

                  {/* Options for select type */}
                  {def.type === 'select' && def.options && (
                    <div className="ml-4 space-y-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Options:</p>
                      {def.options.map((option, optionIndex) => (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <Input
                            value={option}
                            onChange={(e) => handleUpdateOption(def.id, optionIndex, e.target.value)}
                            disabled={!isEditing}
                            className="text-sm flex-1"
                            placeholder="Option value"
                          />
                          {isEditing && (
                            <button
                              onClick={() => handleDeleteOption(def.id, optionIndex)}
                              className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      {isEditing && (
                        <Button
                          onClick={() => handleAddOption(def.id)}
                          size="sm"
                          variant="outline"
                          className="text-xs"
                        >
                          <PlusIcon className="w-3 h-3 mr-1" />
                          Add Option
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Delete button */}
                {isEditing && (
                  <button
                    onClick={() => handleDeleteDefinition(def.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Add button */}
          {isEditing && definitions.length < 5 && (
            <Button
              onClick={handleAddDefinition}
              variant="outline"
              className="w-full"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              Add Label
            </Button>
          )}

          {/* Action buttons */}
          <div className="flex justify-end gap-2 mt-2">
            {isEditing ? (
              <>
                <Button onClick={handleCancel} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700">
                  Save Definitions
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)} variant="outline">
                Edit Definitions
              </Button>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        Define up to 5 custom labels for API keys. These labels will appear as columns in the usage dashboard.
      </p>
    </div>
  );
};